/**
 * @spec docs/superpowers/specs/2026-10-15-staking-ui-wallet-actions-design.md §4.3, §4.4
 * @standard docs/standards/staking-ui-wallet-actions.md#prepare-wallet
 *
 * prepareWallet is called immediately before each wallet prompt (not once per flow). It applies
 * decide(), moves the embedded wallet for transactions only, verifies the chain after any switch,
 * and returns the branded PreparedWallet that sign.ts and send.ts require. It never switches an
 * external wallet: "ask" is a refusal the UI turns into a user-approved switch (useNetworkSwitch).
 *
 * Transactions hold a per-wallet in-tab lock from here until broadcast (send.ts) or failure
 * (sign.ts, send.ts, or a refusal below), so two flows cannot move the shared embedded wallet
 * under each other. The lock is never held through confirmation.
 */
import type { ConnectedWallet, EIP1193Provider } from '@privy-io/react-auth';
import { getChainDisplayName } from '@/lib/chain-registry';
import { parseChainIdHex, WrongNetworkError } from '@/lib/network-gate';
import { getWalletKind, type WalletKind } from '@/lib/wallet-kind';
import { decide } from './decide';
import { PHENOM_WALLET_ONLY_MESSAGE, WalletActionError } from './errors';
import type { WalletAction } from './types';

declare const preparedBrand: unique symbol;

export interface PreparedWallet {
  readonly [preparedBrand]: true;
  readonly wallet: ConnectedWallet;
  readonly walletKind: WalletKind;
  readonly address: `0x${string}`;
  readonly chainId: number | null;
  readonly provider: EIP1193Provider;
  readonly action: WalletAction;
}

/** A lock its flow never released (a hook threw between prepare and broadcast) expires here. */
const LOCK_TIMEOUT_MS = 10 * 60 * 1000;

const lockTails = new Map<string, Promise<void>>();
const heldLocks = new WeakMap<PreparedWallet, () => void>();

async function acquireWalletLock(key: string): Promise<() => void> {
  const previous = lockTails.get(key) ?? Promise.resolve();
  let unlock: () => void = () => undefined;
  const held = new Promise<void>((resolve) => {
    unlock = resolve;
  });
  const tail = previous.then(() => held);
  lockTails.set(key, tail);
  await previous;
  let released = false;
  const release = (): void => {
    if (released) return;
    released = true;
    clearTimeout(timer);
    unlock();
    if (lockTails.get(key) === tail) lockTails.delete(key);
  };
  const timer = setTimeout(release, LOCK_TIMEOUT_MS);
  return release;
}

/** Releases a transaction's lock; a no-op for signatures and for a second call. */
export function releasePreparedWallet(prepared: PreparedWallet): void {
  const release = heldLocks.get(prepared);
  if (!release) return;
  heldLocks.delete(prepared);
  release();
}

async function readChainId(provider: EIP1193Provider): Promise<number | null> {
  try {
    return parseChainIdHex(await provider.request({ method: 'eth_chainId' }));
  } catch {
    return null;
  }
}

function chainOf(action: WalletAction): number {
  if (action.chainId === null) {
    throw new Error(`A ${action.kind} action must name its chain`);
  }
  return action.chainId;
}

async function switchEmbedded(
  wallet: ConnectedWallet,
  walletKind: WalletKind,
  required: number,
  current: number | null,
  provider: EIP1193Provider
): Promise<{ provider: EIP1193Provider; chainId: number }> {
  if (current === required) return { provider, chainId: required };
  const failed = (cause: unknown) =>
    new WalletActionError({
      message: `Your Phenom wallet could not move to ${getChainDisplayName(required)}. Please try again.`,
      phase: 'prepare',
      category: 'unsupported-chain',
      walletKind,
      requiredChainId: required,
      cause,
    });
  try {
    await wallet.switchChain(required);
  } catch (cause) {
    throw failed(cause);
  }
  const switched = await wallet.getEthereumProvider();
  const verified = await readChainId(switched);
  if (verified !== required) {
    throw failed(new Error(`eth_chainId reported ${verified ?? 'nothing'} after the switch`));
  }
  return { provider: switched, chainId: verified };
}

export async function prepareWallet(
  wallet: ConnectedWallet,
  action: WalletAction
): Promise<PreparedWallet> {
  const walletKind = getWalletKind(wallet);
  const release =
    action.kind === 'transaction' ? await acquireWalletLock(wallet.address.toLowerCase()) : null;
  try {
    let provider = await wallet.getEthereumProvider();
    const readsChain =
      action.kind === 'transaction' ||
      (action.kind === 'chain-signature' && walletKind !== 'embedded');
    let chainId = readsChain ? await readChainId(provider) : null;
    const decision = decide(walletKind, chainId, action);

    if (decision === 'block-unsupported') {
      throw new WalletActionError({
        message: PHENOM_WALLET_ONLY_MESSAGE,
        phase: 'prepare',
        category: 'unsupported-account',
        walletKind,
        requiredChainId: action.chainId,
        cause: null,
      });
    }
    if (decision === 'ask' && chainId !== null) {
      const cause = new WrongNetworkError(chainId, chainOf(action));
      throw new WalletActionError({
        message: cause.message,
        phase: 'prepare',
        category: 'wrong_network',
        walletKind,
        requiredChainId: cause.requiredChainId,
        cause,
      });
    }
    if (decision === 'switch-silently') {
      ({ provider, chainId } = await switchEmbedded(
        wallet,
        walletKind,
        chainOf(action),
        chainId,
        provider
      ));
    } else if (action.kind === 'transaction' && chainId === null) {
      // An external wallet would broadcast on whatever chain it is on; refuse instead of guessing.
      const required = chainOf(action);
      throw new WalletActionError({
        message: `We could not read your wallet's network. Make sure it is on ${getChainDisplayName(required)}, then try again.`,
        phase: 'prepare',
        category: 'wrong_network',
        walletKind,
        requiredChainId: required,
        cause: null,
      });
    }

    const prepared = {
      wallet,
      walletKind,
      address: wallet.address as `0x${string}`,
      chainId,
      provider,
      action,
    } as PreparedWallet;
    if (release) heldLocks.set(prepared, release);
    return prepared;
  } catch (error) {
    release?.();
    throw error;
  }
}
